import { useQuery } from '@tanstack/react-query';
import type { CalendarEvent } from '../../../types/calendar';
import { calendarAPI } from '../api/calendar.api';
import type { CalendarEventsDateRange } from '../utils/calendarViewRange';

// Mirrors useBoard/useFunnelTemplates (src/hooks/useLeads.ts): a thin
// useQuery wrapper, keyed by organization, enabled only once one exists.
//
// `range` (see calendarViewRange.ts's getViewDateRange) is BOTH the actual
// server-side filter AND the cache key discriminator: GET /calendar/events
// now takes `from`/`to` (calendar.controller.ts), so a real day/week/month
// view only downloads the events that overlap it, not the org's entire
// history — and the same from/to pair is exactly what should key the cache
// entry, since two calls with an identical range always mean the same data.
// useCreateCalendarEvent/etc. still invalidate with the 3-element
// ['calendar','events',organizationId] prefix, which still matches (and
// clears) every range under that org, because React Query's default
// invalidation is a prefix match, not an exact one.
export function useCalendarEvents(
  organizationId: string | undefined,
  range: CalendarEventsDateRange,
) {
  return useQuery<CalendarEvent[]>({
    queryKey: ['calendar', 'events', organizationId, range.from, range.to],
    queryFn: async () => {
      // `enabled` below keeps this from ever running without one, but the
      // guard (rather than a `!` assertion) means an unexpected call fails
      // with a clear message instead of silently hitting the API with the
      // string "undefined".
      if (!organizationId) {
        throw new Error('useCalendarEvents requires an organizationId');
      }
      const { data } = await calendarAPI.listEvents(organizationId, range);
      return data;
    },
    enabled: !!organizationId,
    staleTime: 60_000,
  });
}
